import React, { memo } from "react";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { useTranslation } from "react-i18next";

import DirectionsWalkIcon from "../../icons/DirectionsWalkIcon";
import SixMinuteWalkAssessmentCard from "./SixMinuteWalkAssessmentCard";

function getSixMinuteWalkStepShape(index, total) {
  if (total <= 1) {
    return "rounded-2xl";
  }

  if (index === 0) {
    return "rounded-t-2xl rounded-b-sm";
  }

  if (index === total - 1) {
    return "rounded-t-sm rounded-b-2xl";
  }

  return "rounded-sm";
}

function SixMinuteWalkStepCard({ icon, title, body, shapeClassName }) {
  return (
    <li
      className={[
        "w-full bg-card px-3 py-3 shadow text-left",
        shapeClassName || "rounded-2xl",
      ].join(" ")}
      aria-label={typeof title === "string" ? title : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center text-primary">
          {icon}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-typography">{title}</p>
          <p className="muted-text text-sm leading-6">{body}</p>
        </div>
      </div>
    </li>
  );
}

export default memo(function SixMinuteWalkIntroCard({
  assessment,
  onStart,
  onContinue,
  onRestart,
}) {
  const { t } = useTranslation();
  const stepCount = 3;

  const hasProgress =
    Boolean(assessment?.preMeasurementId) ||
    Boolean(assessment?.walk) ||
    Boolean(assessment?.postMeasurementId);

  return (
    <SixMinuteWalkAssessmentCard
      title={t(
        "result.sixMinuteWalk.flowTitle",
        "6-Minute Walk Assessment",
      )}
      body={t(
        "result.sixMinuteWalk.flowIntro",
        "This assessment includes a baseline recording, a 6-minute walk test, and a final post-walk recording.",
      )}
      primaryAction={{
        label: hasProgress
          ? t("result.sixMinuteWalk.continueAssessment", "Continue assessment")
          : t("result.sixMinuteWalk.startAssessment", "Start assessment"),
        onClick: hasProgress ? onContinue : onStart,
      }}
      secondaryAction={
        hasProgress
          ? {
              className: "!w-full small-outlined-button",
              label: t("result.sixMinuteWalk.restartAssessment", "Restart"),
              onClick: onRestart,
            }
          : null
      }
    >
      <ol className="m-0 flex list-none flex-col gap-1 p-0 text-left">
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(0, stepCount)}
          icon={
            <VideocamOutlinedIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step1Title", "1. Baseline recording")}
          body={t(
            "result.sixMinuteWalk.step1Body",
            "Record a seated baseline measurement, follow the on-screen prompts, then return to normal and remain still.",
          )}
        />
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(1, stepCount)}
          icon={
            <DirectionsWalkIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step2Title", "2. Timed walk")}
          body={t(
            "result.sixMinuteWalk.step2Body",
            "Walk for 6 minutes with your phone. The app will prompt you to turn around after 3 minutes and will measure the distance travelled.",
          )}
        />
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(2, stepCount)}
          icon={
            <VideocamOutlinedIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step3Title", "3. Post-walk recording")}
          body={t(
            "result.sixMinuteWalk.step3Body",
            "Sit back down and repeat the recording as soon as you are ready, then return to normal and remain still.",
          )}
        />
      </ol>
    </SixMinuteWalkAssessmentCard>
  );
});
